import { Cesium } from 'app/cesium';
import { randomColor } from 'app/lib/random_color';
import { Radio } from 'app/shared/collections/radio';
import { Tag } from 'app/shared/collections/tag';
import { SelectionType } from 'app/shared/selection/SelectionType';
import Constant from 'app/shared/utilities/constant';
import * as _ from 'lodash';
import { IpDeviceResource } from '../resources';
import { Asset } from './asset';
import { ImsObject } from './ims_object';
import { Integration } from './integration';
import { IpDeviceType } from './ip_device_type';

export const IMS_TYPE__IP_DEVICE = 'ip_device';

export class IpDevice extends ImsObject {
    public type: IpDeviceType;
    public radios: {
        [x: string]: Radio;
    };
    public integrations: {
        [x: string]: Integration;
    };
    public selectionType: any;
    public selected: boolean;
    public tags: {
        [x: number]: Tag;
    };
    public new;
    private _uniqueColorString: any;
    private _uniqueCesiumColor: any;
    private _shortName: string;

    constructor(resource = new IpDeviceResource()) {
        super(IMS_TYPE__IP_DEVICE, resource);
        this.type = null;
        this.radios = {};
        this.integrations = {};
        this.selectionType = SelectionType.IP_DEVICE;
        this.selected = false;
        this.tags = {};
    }

    applyResourceChange(resource) {
        super.applyResourceChange(resource);
        this._shortName = _.truncate(this.resource.name, { length: 16 });

        // We should never have a situation where an existing resource does not have a colour.
        // But if we do then display it as white.
        this._uniqueColorString = resource.color || (resource.id ? '#ffffff' : randomColor());
        this._uniqueCesiumColor = Cesium.Color.fromCssColorString(this._uniqueColorString);
    }

    get shortName(): string {
        return this._shortName;
    }

    get name() {
        return this.resource.name;
    }

    set name(newName) {
        this.resource.name = newName;
    }

    get host() {
        return this.resource.host;
    }

    set host(host) {
        this.resource.host = host;
    }

    get monitorIP() {
        return this.resource.monitor_ip;
    }

    set monitorIP(newIP) {
        this.resource.monitor_ip = newIP;
    }

    get mac() {
        return this.resource.mac;
    }

    set mac(mac) {
        this.resource.mac = mac;
    }

    get networkIdType() {
        return this.resource.network_id_type;
    }

    get typeId() {
        return this.resource.type_id;
    }

    set typeId(typeId) {
        this.resource.type_id = typeId;
    }

    get assetId() {
        return this.resource.asset_id;
    }

    set assetId(assetID) {
        this.resource.asset_id = assetID;
    }

    get ignoreStats() {
        return this.resource.ignore_stats;
    }

    set ignoreStats(ignoreStats) {
        this.resource.ignore_stats = ignoreStats;
    }

    // Safe getter for the type name of an ip device
    get typeName(): string {
        return this.type ? this.type.name : 'Unknown';
    }

    get note() {
        return this.resource.note;
    }

    set note(note) {
        this.resource.note = note;
    }

    get ignoredIpDevice() {
        return this.resource.ignore_stats;
    }

    set siteId(siteId) {
        if (this.resource.site_id !== null) {
            this.resource.site_id = siteId;
        } else {
            throw new Error('Site ID already defined');
        }
    }

    get siteId() {
        return this.resource.site_id;
    }

    get isDeleted() {
        return this.resource.is_deleted;
    }

    set isDeleted(isDeleted) {
        this.resource.is_deleted = isDeleted;
    }

    get lastUpdated() {
        return this.resource.last_updated;
    }

    set lastUpdated(lastUpdated) {
        this.resource.last_updated = lastUpdated;
    }

    get typeCesiumColor(): string {
        return this.type ? this.type.cesiumColor : Cesium.Color.WHITE;
    }

    get ipDeviceTemplateId(): number {
        return this.resource.ip_device_template_id;
    }

    set ipDeviceTemplateId(ipDeviceTemplateId: number) {
        this.resource.ip_device_template_id = ipDeviceTemplateId;
    }

    /**
     * Need to use this if we want to update the tag array. DONT use this.tag
     * this.tag is used by collections in order to populate the asset ims object.
     * setTagArray is used by clients of Asset to update the underlying resource
     * object AND it will also update the tag dictionary
     * @param tagArray
     */
    setTagArray(tagArray: Array<Tag>): void {
        let tagIdArray = [];
        let tagLookupById = {};
        _.forEach(tagArray, (tag) => {
            tagIdArray.push(tag.id);
            tagLookupById[tag.id] = tag;
        });
        this.resource.tags = tagIdArray;
        this.tags = tagLookupById;
    }

    getTagArray(): Array<Tag> {
        return _.reduce(
            this.tags,
            (result, tag) => {
                result.push(tag);
                return result;
            },
            [],
        );
    }

    get jsonData() {
        return (this.resource.json_data && this.resource.json_data[this.typeId]) || {};
    }

    set jsonData(json) {
        if (!this.resource.json_data) {
            this.resource.json_data = {};
        }
        this.resource.json_data[this.typeId] = json;
    }

    get parentAssetId() {
        return this.resource.asset_id;
    }

    set parentAssetId(assetID) {
        this.resource.asset_id = assetID;
    }

    get siblingAssetId() {
        return this.resource.sibling_asset_id;
    }

    set siblingAssetId(assetID) {
        this.resource.sibling_asset_id = assetID;
    }

    get qrCode() {
        return this.resource.qr_code;
    }

    set qrCode(newQrCode: string) {
        this.resource.qr_code = newQrCode;
    }

    get uniqueColorString() {
        return this._uniqueColorString;
    }

    get uniqueCesiumColor() {
        return this._uniqueCesiumColor;
    }

    get color() {
        return this.resource.color;
    }

    set color(newColor: string) {
        this.resource.color = newColor;
    }

    clone() {
        let resourceClone = _.clone(this.resource);
        if (this.resource.json_data) {
            resourceClone.json_data = _.cloneDeep(this.resource.json_data);
        }

        let result = new IpDevice(resourceClone);
        result.type = this.type;
        result.radios = _.clone(this.radios);
        result.tags = _.clone(this.tags);
        return result;
    }

    diff(ipDevice) {
        if (ipDevice.constructor === IpDevice) {
            return [
                'id',
                'assetId',
                'ignoreStats',
                'host',
                'mac',
                'monitorIP',
                'name',
                'note',
                'siteId',
                'typeId',
                'tags',
                'jsonData',
                'qrCode',
            ].filter((key) => {
                if (key === 'tags') {
                    return (
                        Object.keys(this.tags).filter(
                            (id) => !Object.keys(ipDevice.tags).includes(id),
                        ).length !== 0 ||
                        Object.keys(this.tags).length !== Object.keys(ipDevice.tags).length
                    );
                }
                if (key === 'jsonData') {
                    return !_.isEqual(this.jsonData, ipDevice.jsonData);
                }
                return ipDevice[key] !== this[key];
            });
        } else {
            throw new Error('Can only diff with an Ip Device');
        }
    }

    /**
     * Some weak validation for an ip device.
     * We rely on the ip device body component to do validation of strings,
     * i.e. is it a valid mac address.
     *
     * In the future we may consider moving the validation out of the component
     * to here if we need this validation to be stronger.
     *
     * @param {IpDevice} ipDevice
     * @returns {bool} True if valid
     */
    static isValid(ipDevice: IpDevice): boolean {
        if (!ipDevice.typeId) {
            return false;
        }
        if (!ipDevice.name) {
            return false;
        }
        // Host should NOT be an IP address.
        if (IpDevice.isValidIPv4(ipDevice.host)) {
            return false;
        }
        return IpDevice.hasValidHostMacOrMonitorIp(ipDevice);
    }

    /**
     * Returns true if there is a valid host, mac or monitor IP
     * Note that this works because if these fields are invalid when being entered in the form,
     * they appear as undefined in here
     * @param ipDevice
     */
    static hasValidHostMacOrMonitorIp(ipDevice: IpDevice): boolean {
        return !!ipDevice.host || !!ipDevice.mac || !!ipDevice.monitorIP;
    }

    /**
     * Returns true if the given IP is a valid IPv4
     * @param ip
     */
    static isValidIPv4(ip: string): boolean {
        return ip != null && ip.match(Constant.IPv4Regex) !== null;
    }

    static toFilterable(ipDevice: IpDevice): object {
        return ipDeviceToFilterableIpDevice(ipDevice, null, null);
    }
}

/**
 * Filterable dictionary
 */
interface FilterableIpDevice {
    id: number;
    name;
    assetId;
    assetName;
    host;
    typeName;
    tagNames;
    desc;
    qrCode;
    mac;
    commentCount;
    ip;
    subText;
    lastUpdated;
}

/**
 * Converts an ipdevice to a filterable ip device. Extracts items
 * from the ip device and its parent asset
 * and turns them into strings
 */
function ipDeviceToFilterableIpDevice(
    ipDevice: IpDevice,
    parentAsset: Asset,
    commentCountsForType: {
        [id: number]: { count: number; countDeleted: number; countWithAttachments: number };
    } = {},
): FilterableIpDevice {
    let subText = undefined;
    let assetName = undefined;
    if (ipDevice.assetId && parentAsset) {
        assetName = parentAsset.name;
        subText = `Asset: ${assetName}`;
    }
    // Appending all of the asset related fields so ip devices are searchable via these fields.
    return {
        id: ipDevice.id,
        name: ipDevice.name,
        assetId: ipDevice.assetId,
        assetName: assetName,
        host: ipDevice.host,
        typeName: ipDevice.type ? ipDevice.type.name : undefined,
        tagNames: Object.values(ipDevice.tags).map((tag) => tag.name),
        desc: ipDevice.note || undefined,
        qrCode: ipDevice.qrCode || undefined,
        mac: ipDevice.mac,
        commentCount:
            commentCountsForType && commentCountsForType[ipDevice.id]
                ? commentCountsForType[ipDevice.id].count
                : undefined,
        ip: ipDevice.monitorIP,
        subText,
        lastUpdated: ipDevice.lastUpdated,
    };
}
